import React from 'react';
import { Popover } from '@patternfly/react-core';
import { 
  RunningIcon, 
  ExclamationTriangleIcon, 
  OutlinedQuestionCircleIcon 
} from '@patternfly/react-icons';

export interface ServiceStatus {
  /** Service/infrastructure name */
  name: string;
  /** Current status */
  status: 'running' | 'outdated' | 'not-deployed';
  /** Optional description for tooltip */
  description?: string;
}

interface StatusLabelProps {
  status: ServiceStatus['status'] | null;
  name?: string;
  description?: string;
}

const statusConfig = {
  running: { 
    text: 'Running', 
    icon: <RunningIcon />, 
    color: 'green' as const, 
    severity: 'success' as const,
    description: 'Service is deployed and running'
  },
  outdated: { 
    text: 'Outdated', 
    icon: <ExclamationTriangleIcon />, 
    color: 'gold' as const, 
    severity: 'warning' as const,
    description: 'Service is deployed but has newer versions available'
  },
  'not-deployed': { 
    text: 'Not Deployed', 
    icon: <OutlinedQuestionCircleIcon />, 
    color: 'purple' as const, 
    severity: 'info' as const,
    description: 'Service is not currently deployed'
  }
};

/**
 * Status label component that displays service/infrastructure status
 * with appropriate colors and icons, similar to Dashboard components
 */
export const StatusLabel: React.FC<StatusLabelProps> = ({ 
  status, 
  name, 
  description 
}) => {
  if (!status) return null;
  
  const config = statusConfig[status];
  const tooltipContent = description || config.description;

  return (
    <Popover
      triggerAction="hover"
      position="right"
      alertSeverityVariant={config.severity}
      headerIcon={config.icon}
      aria-label="Service status"
      headerContent={config.text}
      bodyContent={tooltipContent}
    >
      <span className={`dx-label dx-label--${config.color} dx-label--compact`}>
        <span className="dx-label__icon">{config.icon}</span>
        {config.text}
      </span>
    </Popover>
  );
};

/**
 * Helper function to determine service status based on deployment state
 */
export function getServiceStatus(
  serviceName: string, 
  deployedServices: string[], 
  outdatedServices?: string[]
): ServiceStatus['status'] {
  if (outdatedServices?.includes(serviceName)) {
    return 'outdated';
  }
  if (deployedServices.includes(serviceName)) {
    return 'running';
  }
  return 'not-deployed';
}

/**
 * Helper function to determine infrastructure status
 */
export function getInfrastructureStatus(
  infraName: string,
  deployedServices: string[]
): ServiceStatus['status'] {
  if (deployedServices.includes(infraName)) {
    return 'running';
  }
  return 'not-deployed';
}
